import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { AppThunk } from "../app/store";
import { PageState } from "../api/Types";
import { Deduction } from "../api/Deduction/Deduction";
import { QuoteLineItem } from "../api/Quote/QuoteLineItem";
import { LocalHeroesQuote, Quote, QuoteBalance } from "../api/Quote/Quote";
import { refreshPayments } from "../api/SwyftApi";
import { addAddon, AddonForm, changeQuantityAddon, removeAddon } from "../api/Addon/AddonApi";
import {
  addSubscription,
  QuoteSubscriptionForm,
  removeSubscription
} from "../api/QuoteSubscription/QuoteSubscriptionApi";
import {
  getFinanceIrelandEstimate,
  getLead,
  getLeadByCompletedQuestionnaireId,
  getLocalHeroesQuote,
  getQuote,
  getQuoteBalance,
  getSolarReportOutcome,
  getSolarReportQuote,
  quoteViewedCheckout,
  syncQuoteDeduction
} from "../api/Quote/QuoteApi";
import { releaseQuoteEvent } from "../api/EventSlot/EventSlotApi";
import { SolarReport } from "../api/Quote/SolarReport";
import { FinanceEstimateResponse } from "../api/Quote/FinanceEstimateResponse";
import { Lead } from "../api/Quote/Lead";

interface QuoteState {
  pageState: PageState;
  quoteId?: string | null;
  quote: Quote | null;
  infoAddon: QuoteLineItem | null;
  uneditedQuote: Quote | null;
  error: string | null;
  modalIsOpen: boolean;
  activeDeduction: Deduction | null;
  solarReport: SolarReport | null;
  solarReportParam: { includeBattery: boolean; includeFinance: boolean };
  lead: Lead | null;
  selectedLeadProductId: string | null;
  financeIrelandEstimate: FinanceEstimateResponse | null;
  localHeroesQuote: LocalHeroesQuote | null;
  balance: QuoteBalance | null;
  balanceLoading: boolean;
  balanceError: string | null;
}

const initialState: QuoteState = {
  pageState: PageState.Loading,
  quote: null,
  uneditedQuote: null,
  infoAddon: null,
  quoteId: null,
  error: null,
  modalIsOpen: false,
  activeDeduction: null,
  solarReport: null,
  solarReportParam: { includeBattery: false, includeFinance: false },
  lead: null,
  selectedLeadProductId: null,
  financeIrelandEstimate: null,
  localHeroesQuote: null,
  balance: null,
  balanceLoading: false,
  balanceError: null
};

function startLoading(state: QuoteState) {
  state.pageState = PageState.Loading;
}

function loadingFailed(state: QuoteState, action: PayloadAction<string>) {
  state.pageState = PageState.Error;
  state.error = action.payload;
}

const quote = createSlice({
  name: "quote",
  initialState,
  reducers: {
    getQuoteStart: startLoading,
    addAddonStart: startLoading,
    changeQuantityAddonStart: startLoading,
    removeAddonStart: startLoading,
    addSubscriptionStart: startLoading,
    removeSubscriptionStart: startLoading,
    getQuoteFailure: loadingFailed,
    addAddonFailure: loadingFailed,
    changeQuantityAddonFailure: loadingFailed,
    removeAddonFailure: loadingFailed,
    addSubscriptionFailure: loadingFailed,
    removeSubscriptionFailure: loadingFailed,
    getQuoteSuccess(state, { payload }: PayloadAction<Quote>) {
      state.pageState = PageState.Ready;
      state.quoteId = payload.id;
      state.quote = payload;
      state.uneditedQuote = payload;
      state.modalIsOpen = false;
      state.error = null;
      state.infoAddon = state.infoAddon
        ? state.quote.addons?.filter((i) => i.productCode === state.infoAddon?.productCode)[0] ?? null
        : null;
      state.lead = null;
    },
    getSolarReportSuccess(
      state,
      { payload }: PayloadAction<{ data: SolarReport; param: { includeBattery: boolean; includeFinance: boolean } }>
    ) {
      state.solarReport = payload.data;
      state.solarReportParam = payload.param;
    },
    getSolarReportFailure(state) {
      state.solarReport = null;
    },
    getLeadSuccess(state, { payload }: PayloadAction<{ data: Lead }>) {
      state.lead = payload.data;
      state.selectedLeadProductId = payload.data.recommendedProducts[0].id;
      state.quote = null;
    },
    getLeadFailure(state) {
      state.lead = null;
    },
    setSolarReportParam(state, { payload }: PayloadAction<{ includeBattery: boolean; includeFinance: boolean }>) {
      state.solarReportParam = payload;
    },
    getFinanceEstimateSuccess(state, { payload }: PayloadAction<FinanceEstimateResponse>) {
      state.financeIrelandEstimate = payload;
    },
    getLocalHeroesQuoteSuccess(state, { payload }: PayloadAction<LocalHeroesQuote>) {
      state.localHeroesQuote = payload;
    },
    customerTextUpdated(state, { payload }: PayloadAction<string>) {
      state.quote && (state.quote.customerText = payload);
    },
    requestInstallationDateUpdated(state, { payload }: PayloadAction<string | null>) {
      state.quote && (state.quote.requestedInstallationDate = payload);
    },
    openModal(state) {
      state.modalIsOpen = true;
    },
    closeModal(state) {
      state.modalIsOpen = false;
    },
    showDeductionInfoModal(state, { payload }: PayloadAction<Deduction>) {
      state.activeDeduction = payload;
    },
    closeDeductionInfoModal(state) {
      state.activeDeduction = null;
    },
    setInfoAddon(state, { payload }: PayloadAction<QuoteLineItem>) {
      state.infoAddon = payload;
    },
    toggleQuoteGraphVisibility(state, { payload }: PayloadAction<boolean>) {
      state.quote && (state.quote.isGraphVisible = payload);
    },
    setSelectedLeadProductId(state, { payload }: PayloadAction<string>) {
      state.selectedLeadProductId = payload;
    },
    getQuoteBalanceStart(state) {
      state.balanceLoading = true;
    },
    getQuoteBalanceSuccess(state, { payload }: PayloadAction<QuoteBalance>) {
      state.balanceError = null;
      state.balanceLoading = false;
      state.balance = payload;
    },
    getQuoteBalanceFailure(state) {
      state.balanceError = "Could not load quote balance";
      state.balanceLoading = false;
      state.balance = null;
    }
  }
});

export const {
  addAddonStart,
  addSubscriptionStart,
  removeSubscriptionStart,
  removeAddonStart,
  changeQuantityAddonStart,
  getQuoteStart,
  getQuoteSuccess,
  getSolarReportSuccess,
  getSolarReportFailure,
  getLeadSuccess,
  getLeadFailure,
  addAddonFailure,
  removeAddonFailure,
  changeQuantityAddonFailure,
  getQuoteFailure,
  addSubscriptionFailure,
  removeSubscriptionFailure,
  openModal,
  closeModal,
  closeDeductionInfoModal,
  showDeductionInfoModal,
  customerTextUpdated,
  setInfoAddon,
  requestInstallationDateUpdated,
  toggleQuoteGraphVisibility,
  getFinanceEstimateSuccess,
  getLocalHeroesQuoteSuccess,
  setSolarReportParam,
  setSelectedLeadProductId,
  getQuoteBalanceStart,
  getQuoteBalanceSuccess,
  getQuoteBalanceFailure
} = quote.actions;

export default quote.reducer;

export const addSubscriptionQuote =
  (quoteId: string, request: QuoteSubscriptionForm): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addSubscriptionStart());
      const quote = await addSubscription(quoteId, request);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(addSubscriptionFailure(err.toString()));
    }
  };

export const removeSubscriptionQuote =
  (quoteId: string, request: QuoteSubscriptionForm): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(removeSubscriptionStart());
      const quote = await removeSubscription(quoteId, request);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(removeSubscriptionFailure(err.toString()));
    }
  };

export const addAddonQuote =
  (quoteId: string, addon: AddonForm): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(addAddonStart());
      const quote = await addAddon(quoteId, addon);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(addAddonFailure(err.toString()));
    }
  };

export const changeQuantityAddonQuote =
  (quoteId: string, addon: AddonForm): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(changeQuantityAddonStart());
      const quote = await changeQuantityAddon(quoteId, addon);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(changeQuantityAddonFailure(err.toString()));
    }
  };

export const removeAddonQuote =
  (quoteId: string, addon: AddonForm): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(removeAddonStart());
      await removeAddon(quoteId, addon);
      const quote = await syncQuoteDeduction(quoteId, false);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(removeAddonFailure(err.toString()));
    }
  };

export const fetchQuote =
  (quoteId: string, updatePayments: boolean = false): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getQuoteStart());
      if (updatePayments) {
        await refreshPayments(quoteId);
      }
      const quote = await getQuote(quoteId);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(getQuoteFailure(err.toString()));
    }
  };

export const fetchBalance =
  (quoteId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getQuoteBalanceStart());
      const quoteBalance = await getQuoteBalance(quoteId);
      dispatch(getQuoteBalanceSuccess(quoteBalance));
    } catch (err: any) {
      dispatch(getQuoteBalanceFailure(err.toString()));
    }
  };

export const syncQuote =
  (quoteId: string, autoApply: boolean): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getQuoteStart());
      const quote = await syncQuoteDeduction(quoteId, autoApply);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(getQuoteFailure(err.toString()));
    }
  };

export const releaseQuote =
  (quoteId: string): AppThunk =>
  async (dispatch) => {
    try {
      dispatch(getQuoteStart());
      const quote = await releaseQuoteEvent(quoteId);
      dispatch(getQuoteSuccess(quote));
    } catch (err: any) {
      dispatch(getQuoteFailure(err.toString()));
    }
  };

export const viewedCheckout =
  (quoteId: string): AppThunk =>
  async (dispatch) => {
    try {
      await quoteViewedCheckout(quoteId);
    } catch (err: any) {}
  };

export const fetchSolarReport =
  ({
    quoteId,
    includeBattery,
    includeFinance
  }: {
    quoteId: string;
    includeBattery?: boolean;
    includeFinance?: boolean;
  }): AppThunk =>
  async (dispatch, getState) => {
    try {
      const prevParam = getState().quote.solarReportParam;
      dispatch(
        setSolarReportParam({
          includeBattery: includeBattery !== undefined ? includeBattery : prevParam.includeBattery,
          includeFinance: includeFinance !== undefined ? includeFinance : prevParam.includeFinance
        })
      );
      const report = await getSolarReportQuote({
        quoteId,
        ...prevParam,
        includeBattery: includeBattery !== undefined ? includeBattery : prevParam.includeBattery,
        includeFinance: includeFinance !== undefined ? includeFinance : prevParam.includeFinance
      });
      if (report.productionAnnualKwh === 0) {
        dispatch(getSolarReportFailure());
        return;
      }
      dispatch(
        getSolarReportSuccess({
          data: report,
          param: {
            includeBattery: includeBattery !== undefined ? includeBattery : prevParam.includeBattery,
            includeFinance: includeFinance !== undefined ? includeFinance : prevParam.includeFinance
          }
        })
      );
    } catch (err: any) {
      dispatch(getSolarReportFailure());
    }
  };

export const fetchLeadReport =
  ({
    leadId,
    productId,
    includeFinance
  }: {
    leadId: string;
    productId?: string | null;
    includeFinance?: boolean;
  }): AppThunk =>
  async (dispatch, getState) => {
    try {
      const prevParam = getState().quote.solarReportParam;
      dispatch(
        setSolarReportParam({
          includeBattery: prevParam.includeBattery,
          includeFinance: includeFinance !== undefined ? includeFinance : prevParam.includeFinance
        })
      );
      const report = await getSolarReportOutcome({
        leadId,
        productId,
        ...prevParam,
        includeFinance: includeFinance !== undefined ? includeFinance : prevParam.includeFinance
      });
      if (report.productionAnnualKwh === 0) {
        dispatch(getSolarReportFailure());
        return;
      }
      dispatch(
        getSolarReportSuccess({
          data: report,
          param: {
            includeBattery: prevParam.includeBattery,
            includeFinance: includeFinance !== undefined ? includeFinance : prevParam.includeFinance
          }
        })
      );
    } catch (err: any) {
      dispatch(getSolarReportFailure());
    }
  };

export const fetchLead =
  ({ leadId, completedQuestionnaireId }: { leadId?: string; completedQuestionnaireId?: string }): AppThunk =>
  async (dispatch) => {
    try {
      var lead: Lead | null = null;
      if (leadId) {
        lead = await getLead({ leadId });
      } else if (completedQuestionnaireId) {
        lead = await getLeadByCompletedQuestionnaireId({ completedQuestionnaireId });
      }
      dispatch(
        getLeadSuccess({
          data: lead!
        })
      );
    } catch (err: any) {
      dispatch(getLeadFailure());
    }
  };

export const fetchFinanceIrelandEstimate =
  (amount: number): AppThunk =>
  async (dispatch) => {
    try {
      const financeIrelandEstimate = await getFinanceIrelandEstimate(amount);
      dispatch(getFinanceEstimateSuccess(financeIrelandEstimate));
    } catch (err: any) {}
  };

export const fetchLocalHeroesQuote =
  (quoteId: string): AppThunk =>
  async (dispatch) => {
    try {
      const localHeroesQuote = await getLocalHeroesQuote(quoteId);
      dispatch(getLocalHeroesQuoteSuccess(localHeroesQuote));
    } catch (err: any) {}
  };
